import { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../../services/api';
import { dateOf, plural } from '../../nucleus/format';

// GROUPS — the groups you are in and the ones you are invited to. Your own
// space only: a group's own management lives inside it (/group/<id>/…).

const byName = (a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase());

// YOUR TWO CONTRACTS with a group, as the list carries them (GET /groups):
// what you share with it (your member → group contract; `realms` empty when it
// shares nothing) and what you can do in it (the group's contract with you).
// Read off the contracts, so a creator and an invitee read the same.
export function shareOf(s) {
    return s ? { epId: String(s.ep_id), realms: s.realm_names || [], realmIds: (s.realm_ids || []).map(String), active: s.active !== false } : null;
}
export function accessOf(a) {
    return a ? {
        epId: String(a.ep_id), realm: a.realm_name || null, realmId: a.realm_id ? String(a.realm_id) : null, kind: a.realm_kind || null,
        // how it stands: active, paused, expired, revoked (null: the list does not say)
        state: a.state || null,
    } : null;
}
// "Work, Photos", "Work (paused)" for a paused contract, which grants nothing, or "Nothing"
export const sharesWords = (share) => (share?.realms?.length
    ? `${share.realms.join(', ')}${share.active === false ? ' (paused)' : ''}` : 'Nothing');
export const sharing = (g) => !!g.share?.realms?.length;

export async function loadGroups() {
    const groups = (await api.getGroups().catch(() => ({ groups: [] })))?.groups || [];
    return groups.map((g) => ({
        id: String(g.id),
        name: g.name || 'Group',
        role: g.my_role || g.role || 'member',
        count: g.member_count ?? null,
        description: g.description || '',
        created: g.created_at || null,
        createdBy: g.created_by_user_id ? String(g.created_by_user_id) : null,
        joined: g.joined_at || g.my_joined_at || null,
        encrypted: !!g.encryption_enabled,
        avatar: g.avatar_url || null,
        share: shareOf(g.my_share),
        access: accessOf(g.my_access),
    })).sort(byName);
}

export async function loadInvitations() {
    const r = await api.getIncomingInvitations().catch(() => ({ invitations: [] }));
    return (r?.invitations || []).filter((i) => (i.status || 'pending') === 'pending').map((i) => ({
        id: String(i.id),
        groupId: String(i.group_id),
        name: i.group_name || 'A group',
        description: i.group_description || '',
        by: i.invited_by_full_name || i.invited_by_name || null,
        byId: i.invited_by_user_id ? String(i.invited_by_user_id) : null,
        role: i.role || 'member',
        at: i.created_at || null,
        // what they could do there: the realm the inviter picked (an admin: Full Access)
        realm: i.role === 'admin' ? { name: 'Full Access', kind: 'full_access' } : (i.realm || null),
        // you pressed Join and left the consent page: still waiting, and answerable
        started: !!i.started,
        startedAt: i.started ? i.responded_at || null : null,
    })).sort(byName);
}

export function useGroupsData() {
    const [state, setState] = useState(null);
    const load = useCallback(() => Promise.all([loadGroups(), loadInvitations()]).then(([groups, invitations]) => setState({ groups, invitations })), []);
    useEffect(() => {
        let live = true;
        Promise.all([loadGroups(), loadInvitations()]).then(([groups, invitations]) => { if (live) setState({ groups, invitations }); });
        return () => { live = false; };
    }, []);
    return useMemo(() => ({
        loaded: state !== null,
        groups: state?.groups || [],
        invitations: state?.invitations || [],
        reload: load,
    }), [state, load]);
}

export const sizeOf = (n) => (n == null ? null : plural(n, 'person', 'people'));

// LEAVING A GROUP, said as it will happen: both your contracts with it end.
// The plain confirm, for a member or an admin beside other admins.
export function leaveConfirm({ name }) {
    return {
        title: `Leave ${name}?`,
        body: `You lose access to its graph. Both your contracts with ${name} end: what you share with it, and what you can do in it. An admin can invite you back.`,
        label: 'Leave',
    };
}

// Whether you are its last admin, by the member list ({ others, heir }), or null
// (not an admin, another admin stays, or the list is not known). The heir is the
// server's pick: the earliest to join of those who stay.
export function lastAdminOf({ role, members, meId }) {
    if (role !== 'admin' || !members) return null;
    const others = members.filter((m) => String(m.id) !== String(meId));
    if (others.some((m) => m.role === 'admin')) return null;
    const dated = others.filter((m) => m.joined).sort((a, b) => new Date(a.joined) - new Date(b.joined));
    return { others, heir: dated.length === others.length ? dated[0] || null : null };
}

// THE LAST ADMIN CHOOSES, at the moment of leaving: transfer it to the
// longest-standing member, or close it — the same cleanup as Delete. With
// nobody else in it, Close is the only choice. A confirm with `choices`
// resolves to the one picked.
export function lastAdminAsk(name, { heir = null, choices = ['transfer', 'close'] } = {}) {
    const heirName = heir?.name || 'its longest-standing member';
    const since = heir?.joined && dateOf(heir.joined) ? ` (${dateOf(heir.joined)})` : '';
    return {
        title: `You are ${name}’s last admin`,
        label: 'Leave',
        choices: {
            label: `What happens to ${name}`,
            initial: choices.includes('transfer') ? 'transfer' : 'close',
            options: [
                choices.includes('transfer') && {
                    value: 'transfer',
                    title: `Transfer to ${heirName}`,
                    text: `${heir ? `${heirName} joined first${since}. They become` : 'They become'} its admin, on Full Access. ${name} carries on.`,
                    confirmLabel: 'Transfer and leave',
                },
                {
                    value: 'close',
                    title: 'Close the group',
                    text: 'Closing cleans up exactly like Delete: its apps are disconnected, its public page comes down, its realms and invitations are removed, and every member contract ends. This can’t be undone.',
                    confirmLabel: 'Close and leave',
                },
            ].filter(Boolean),
        },
        body: `Either way you leave, and both your contracts with ${name} end.`,
    };
}

// What Delete (and Close) cleans up, said once for every place that asks.
export const deleteConfirm = (name) => ({
    title: `Delete ${name}?`,
    body: 'Its graph goes, and everything it set up: its apps are disconnected, its public page comes down, its realms and invitations are removed, and every member contract and connection ends, for everyone in it. This cannot be undone.',
    typed: 'DELETE',
    label: 'Delete',
});

// LEAVE, the one way: the leave action's confirm is the plain one unless you are
// its last admin (`leaveAsks` false), when this asks the choice itself. Anyone else
// asks the server, which answers with the choices (409 LAST_ADMIN_CHOICE) only if
// they are the last admin — the role here may be stale (the group passed to you
// since). A choice made from a stale list (Transfer, when nobody else is left) is
// answered with the server's choices, and asked again. Resolves true when you left.
export const leaveAsks = (opts) => !lastAdminOf(opts);
export async function leaveGroup(groupId, meId, confirm, { name = 'this group', role, members } = {}) {
    const asked = (e) => lastAdminAsk(name, {
        heir: e.heir ? { name: e.heir.username, joined: e.heir.joined_at } : null,
        choices: e.choices || ['close'],
    });
    const choose = async (ask, retry) => {
        const choice = await confirm(ask);
        if (!choice) return false;
        try {
            await api.removeGroupMember(groupId, meId, { lastAdminChoice: choice });
        } catch (e) {
            if (!retry || (e?.code !== 'NOBODY_TO_TRANSFER' && e?.code !== 'LAST_ADMIN_CHOICE')) throw e;
            return choose(asked(e), false);
        }
        return true;
    };
    const last = lastAdminOf({ role, members, meId });
    if (last) return choose(lastAdminAsk(name, { heir: last.heir, choices: last.others.length ? ['transfer', 'close'] : ['close'] }), true);
    try {
        await api.removeGroupMember(groupId, meId, { lastAdminChoice: 'ask' });
        return true;
    } catch (e) {
        if (e?.code !== 'LAST_ADMIN_CHOICE') throw e;
        return choose(asked(e), false);
    }
}
